import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useLocale } from 'next-intl'
import type { paths } from 'user-api/src/generated/openApi/schema'
import { ApiResponseBody, fetchClient, userApiClient } from '@/setup/api'

export const DEFAULT_BLOG_PAGE = 1

type BlogAllResponse = ApiResponseBody<'get', '/web/blog/content'>

export type IBlogAllResponseData = BlogAllResponse['data']

export type BlogPostPreview = IBlogAllResponseData[number]

export type IGetAllBlogsQueryParams = NonNullable<
    paths['/web/blog/content']['get']['parameters']['query']
>

const buildInit = (params: IGetAllBlogsQueryParams | undefined, locale: string) => ({
    params: {
        query: {
            topicId: params?.topicId ?? undefined,
            page: params?.page ?? DEFAULT_BLOG_PAGE,
        },
    },
    headers: { 'accept-language': locale },
})

export const getAllBlogsQueryOptions = (
    params: IGetAllBlogsQueryParams | undefined,
    locale: string
) => userApiClient.queryOptions('get', '/web/blog/content', buildInit(params, locale))

export function useGetAllBlogsQuery(params?: IGetAllBlogsQueryParams) {
    const locale = useLocale()

    return useQuery(getAllBlogsQueryOptions(params, locale))
}

const getNextPageParam = (lastPage: BlogAllResponse | undefined) => {
    if (!lastPage) return DEFAULT_BLOG_PAGE

    if (lastPage.meta.isLastPage) return null

    if (lastPage.data.length === 0) return null

    return Number(lastPage.meta.page) + 1
}

/**
 * openapi-react-query ships `queryOptions` but no `infiniteQueryOptions`, so the key
 * and the page-param injection its `useInfiniteQuery` does internally are reproduced
 * here once, and shared by the hook and the server prefetch. Writing them twice would
 * let the dehydrated pages land on a different key than the hook reads.
 */
export const getAllBlogsInfiniteQueryOptions = (
    params: Omit<IGetAllBlogsQueryParams, 'page'>,
    locale: string
) => {
    const init = buildInit(params, locale)

    const { queryKey } = userApiClient.queryOptions('get', '/web/blog/content', init)

    return {
        // Must not collide with `getAllBlogsQueryOptions` on the same path: infinite
        // queries cache `{ pages, pageParams }` while a plain query caches the bare
        // envelope, so sharing a key makes `data.pages` undefined and react-query
        // throws on `pages.length`. Safe to append because the queryFn below closes
        // over `init` rather than reading it back off the key.
        // https://github.com/TanStack/query/discussions/7455#discussioncomment-9481854
        queryKey: [...queryKey, 'infinite'],
        initialPageParam: DEFAULT_BLOG_PAGE,
        getNextPageParam,
        queryFn: async ({ pageParam, signal }: { pageParam: number; signal: AbortSignal }) => {
            const { data, error } = await fetchClient.GET('/web/blog/content', {
                ...init,
                signal,
                params: {
                    ...init.params,
                    query: { ...init.params.query, page: pageParam },
                },
            })

            if (error) throw error

            return data
        },
    }
}

export function useGetAllBlogsInfiniteQuery(params: Omit<IGetAllBlogsQueryParams, 'page'>) {
    const locale = useLocale()

    return useInfiniteQuery(getAllBlogsInfiniteQueryOptions(params, locale))
}
